import React, { useState } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import Card from '../../../common/Card';
import Pagination from '../../../common/Pagination';

const FamilleTypeArticleList = ({
  familleTypeArticles,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete
}) => {
  const { isDarkMode } = useTheme();
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  const getFamilleColor = (designation) => {
    if (isDarkMode) return 'bg-gray-700 text-gray-200 border-gray-600';
    const colorMap = {
      'RIA': 'bg-blue-100 text-blue-800 border-blue-200',
      'Extincteur': 'bg-red-100 text-red-800 border-red-200',
      'PIA': 'bg-green-100 text-green-800 border-green-200',
      'AUTO': 'bg-purple-100 text-purple-800 border-purple-200',
      'UMM': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colorMap[designation] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getFamilleIcon = (designation) => {
    const iconMap = {
      'RIA': 'fas fa-fire-extinguisher',
      'Extincteur': 'fas fa-spray-can',
      'PIA': 'fas fa-shield-alt',
      'AUTO': 'fas fa-cog',
      'UMM': 'fas fa-tools'
    };
    return iconMap[designation] || 'fas fa-tag';
  };

  // Add a function to return icon color classes for table view
  const getFamilleIconColor = (designation) => {
    if (isDarkMode) {
      // Use colored icons even in dark mode for table
      const colorMap = {
        'RIA': 'text-blue-400',
        'Extincteur': 'text-red-400',
        'PIA': 'text-green-400',
        'AUTO': 'text-purple-400',
        'UMM': 'text-orange-400'
      };
      return colorMap[designation] || 'text-gray-400';
    } else {
      const colorMap = {
        'RIA': 'text-blue-600',
        'Extincteur': 'text-red-600',
        'PIA': 'text-green-600',
        'AUTO': 'text-purple-600',
        'UMM': 'text-orange-600'
      };
      return colorMap[designation] || 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Chargement des familles de types d'articles...</p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!familleTypeArticles || familleTypeArticles.length === 0) {
    return (
      <Card>
        <div className="p-6">
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
            <div className={`mx-auto h-24 w-24 rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <i className="fas fa-sitemap text-gray-400 text-2xl"></i>
            </div>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Aucune famille de type d'article
            </h3>
            <p className="text-gray-600 mb-6">
              Commencez par créer votre première famille de type d'article.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle & Results Info */}
      <Card>
        <div className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className={`flex items-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <i className="fas fa-info-circle mr-2"></i>
              <span>
                {pagination.total} famille{pagination.total > 1 ? 's' : ''} au total
                {pagination.total > pagination.pageSize && (
                  <span className="ml-1">
                    • Page {pagination.page} sur {pagination.maxPage}
                  </span>
                )}
              </span>
            </div>
            
            {/* View Mode Toggle */}
            <div className={`flex rounded-lg p-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'cards'
                    ? isDarkMode
                      ? 'bg-gray-600 text-white shadow-sm'
                      : 'bg-white text-primary-600 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-th-large mr-1.5"></i>
                Cartes
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-list mr-1.5"></i>
                Liste
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {familleTypeArticles.map((familleTypeArticle) => (
            <Card key={familleTypeArticle.id} className="hover:shadow-lg transition-shadow duration-200">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-3 ${getFamilleColor(familleTypeArticle.designation)}`}>
                      <i className={`${getFamilleIcon(familleTypeArticle.designation)} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {familleTypeArticle.designation}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {familleTypeArticle.id}</p>
                    </div>
                  </div>
                </div>

                {/* Badge */}
                <div className="mb-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getFamilleColor(familleTypeArticle.designation)}`}>
                    <div className="w-2 h-2 rounded-full bg-current mr-1.5"></div>
                    Famille de type
                  </span>
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => onView(familleTypeArticle)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    <i className="fas fa-eye mr-1"></i>
                    Voir
                  </button>
                  <button
                    onClick={() => onEdit(familleTypeArticle)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    <i className="fas fa-edit mr-1"></i>
                    Modifier
                  </button>
                  <button
                    onClick={() => onDelete(familleTypeArticle)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                    }`}>
                      Famille
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                    }`}>
                      ID
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                    }`}>
                      Type
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {familleTypeArticles.map((familleTypeArticle) => (
                    <tr key={familleTypeArticle.id} className={`transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-800 hover:bg-gray-700 divide-gray-700' 
                        : 'bg-white hover:bg-gray-50 divide-gray-200'
                    }`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <i className={`${getFamilleIcon(familleTypeArticle.designation)} text-xl ${getFamilleIconColor(familleTypeArticle.designation)}`}></i>
                          </div>
                          <div>
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {familleTypeArticle.designation}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>#{familleTypeArticle.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getFamilleColor(familleTypeArticle.designation)}`}>
                          <div className="w-2 h-2 rounded-full bg-current mr-1.5"></div>
                          Famille de type
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => onView(familleTypeArticle)}
                            className={`p-1 rounded ${isDarkMode ? 'text-primary-400 hover:text-white' : 'text-primary-600 hover:text-primary-900'}`}
                            title="Voir les détails"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            onClick={() => onEdit(familleTypeArticle)}
                            className={`p-1 rounded ${isDarkMode ? 'text-amber-400 hover:text-white' : 'text-amber-600 hover:text-amber-900'}`}
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => onDelete(familleTypeArticle)}
                            className={`p-1 rounded ${isDarkMode ? 'text-red-400 hover:text-white' : 'text-red-600 hover:text-red-900'}`}
                            title="Supprimer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination.maxPage > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.maxPage}
          pageSize={pagination.pageSize}
          totalItems={pagination.total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default FamilleTypeArticleList;